import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CalendarDays, Plus, Ticket, Search } from 'lucide-react';
import { calendarAPI } from '@/lib/api';
import { toast } from 'sonner';
import EmptyState from '@/components/accounting/EmptyState';
import TableSkeleton from '@/components/accounting/TableSkeleton';
import { useAuth } from '@/context/AuthContext';
import { canCreateEvents } from '@/lib/utils';
import EventCover from '@/components/calendar/EventCover';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import { formatEventDateTime } from '@/lib/eventTime';

// Phase 26.9.1 — small cover thumbnail for the internal Events list rows.
const EventListRowCover = ({ event }) => {
  const url = useInternalCoverBlob(event?.cover_image_url);
  return <EventCover coverUrl={url} title={event?.title || 'Event'} category={event?.category} variant="thumb" />;
};

const STATUS_TONE = {
  draft:      'bg-slate-100 text-slate-700 border-slate-300',
  published:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled:  'bg-red-100 text-red-800 border-red-300',
  completed:  'bg-blue-100 text-blue-800 border-blue-300',
  archived:   'bg-neutral-100 text-neutral-700 border-neutral-300',
};

const STATUS_FILTERS = [null, 'draft', 'published', 'cancelled', 'completed', 'archived'];

function fmtDate(iso, tz) {
  return formatEventDateTime(iso, tz);
}

const humanize = (v) => (v || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function EventsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = canCreateEvents(user);
  const [sp, setSp] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enums, setEnums] = useState({ event_types: [], business_categories: [], languages: [], statuses: [] });
  const [q, setQ] = useState('');
  const status = sp.get('status') || '';
  const eventType = sp.get('type') || '';
  const businessCategory = sp.get('business_category') || '';
  const language = sp.get('language') || '';

  useEffect(() => {
    calendarAPI.enums().then((r) => setEnums(r.data || {})).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, include_archived: status === 'archived' };
      if (status) params.status = status;
      const { data } = await calendarAPI.listEvents(params);
      let items = data.items || [];
      if (eventType) items = items.filter((e) => e.event_type === eventType);
      if (businessCategory) items = items.filter((e) => e.business_category === businessCategory);
      if (language) items = items.filter((e) => e.language === language);
      if (q.trim()) {
        const s = q.toLowerCase();
        items = items.filter((e) =>
          (e.title || '').toLowerCase().includes(s) ||
          (e.description || '').toLowerCase().includes(s) ||
          (e.physical_location?.venue_name || '').toLowerCase().includes(s) ||
          (e.owner_display_name || '').toLowerCase().includes(s)
        );
      }
      setRows(items);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load events');
    } finally { setLoading(false); }
  }, [status, eventType, businessCategory, language, q]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    setSp(next, { replace: true });
  };

  return (
    <div className="p-6 space-y-6" data-testid="events-list-page">
      <PageHeader
        title="Events & Calendar"
        description={`${rows.length} event${rows.length === 1 ? '' : 's'} matching filters`}
        breadcrumbs={[{ label: 'Events & Calendar', to: '/events-calendar/dashboard' }, { label: 'Events' }]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/calendar')}>
              <CalendarDays className="h-4 w-4 mr-2" /> Calendar View
            </Button>
            {canCreate && (
              <Button onClick={() => navigate('/calendar?create=1')} data-testid="create-event-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Event
              </Button>
            )}
          </div>
        }
      />

      {/* Search + filters */}
      <div className="space-y-2">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, description, venue, organizer…"
                 className="pl-8" data-testid="events-search" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button key={f || 'all'} onClick={() => setFilter('status', f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${(status || '') === (f || '') ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`event-status-filter-${f || 'all'}`}>
              {f ? humanize(f) : 'All'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={eventType || 'any'} onValueChange={(v) => setFilter('type', v === 'any' ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="event-type-filter"><SelectValue placeholder="Event Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Event Types</SelectItem>
              {(enums.event_types || []).map((t) => <SelectItem key={t} value={t}>{humanize(t)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={businessCategory || 'any'} onValueChange={(v) => setFilter('business_category', v === 'any' ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="business-category-filter"><SelectValue placeholder="Business Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Categories</SelectItem>
              {(enums.business_categories || []).map((t) => <SelectItem key={t} value={t}>{humanize(t)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={language || 'any'} onValueChange={(v) => setFilter('language', v === 'any' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="language-filter"><SelectValue placeholder="Language" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Languages</SelectItem>
              {(enums.languages || []).map((t) => <SelectItem key={t} value={t}>{humanize(t)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} cols={10} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Ticket}
              title="No Events Found"
              description={q || status || eventType || businessCategory || language ? 'No events match the current filters.' : 'No events have been created yet.'}
              action={canCreate ? <Button size="sm" onClick={() => navigate('/calendar?create=1')}><Plus className="h-4 w-4 mr-2" /> Create Event</Button> : null}
              dataTestId="events-empty-state"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-16"></th>
                    <th className="text-left px-3 py-2 font-medium">Event</th>
                    <th className="text-left px-3 py-2 font-medium">Event Type</th>
                    <th className="text-left px-3 py-2 font-medium">Business Category</th>
                    <th className="text-left px-3 py-2 font-medium">Language</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Organizer</th>
                    <th className="text-left px-3 py-2 font-medium">Start</th>
                    <th className="text-left px-3 py-2 font-medium">End</th>
                    <th className="text-left px-3 py-2 font-medium">Audience</th>
                    <th className="text-right px-3 py-2 font-medium">Capacity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.event_id}
                        onClick={() => navigate(`/portal/events/${e.event_id}`)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        data-testid={`event-row-${e.event_id}`}>
                      <td className="px-3 py-2" data-testid={`event-row-cover-${e.event_id}`}>
                        <div className="w-14">
                          <EventListRowCover event={e} key={e.cover_image_url || 'none'} />
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800">{e.title}</td>
                      <td className="px-3 py-2">
                        {e.event_type ? <Badge variant="outline">{humanize(e.event_type)}</Badge> : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {e.business_category ? <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">{humanize(e.business_category)}</Badge> : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2" data-testid={`event-language-${e.event_id}`}>
                        {e.language ? <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-800">{humanize(e.language)}</Badge> : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`${STATUS_TONE[e.status] || 'bg-slate-100 text-slate-700 border-slate-300'} border font-normal`}>{humanize(e.status)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{e.owner_display_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">{fmtDate(e.start_datetime, e.timezone)}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">{fmtDate(e.end_datetime, e.timezone)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">{humanize(e.visibility_scope)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">{e.capacity_max ?? '∞'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
